/** @jsx jsx **/
import { Fragment } from 'react'

import { jsx, Box, Text, Flex } from 'theme-ui'

import { space } from '~/gatsby-plugin-theme-ui'

import { Maybe, ProductItemComponentType, ProductVariantType } from '~/@types/models'

import { useCustomerContext } from '~/context/customer-context'

import CompareAtPrice from '~/components/compare-at-price'

import useMediaQuery from '~/hooks/use-media-query'

import { isValidTradeCustomerDiscount } from '~/utils/is-valid-trade-customer-discount'
import { formatMoney } from '~/utils/format'

type ProductVariantInfoType = {
  product: ProductItemComponentType
  productVariant?: Maybe<ProductVariantType>
  isStartingAt?: boolean
  isFabric?: boolean
}
const useProductVariantInfo = ({
  product,
  productVariant,
  isStartingAt = false,
  isFabric,
}: ProductVariantInfoType) => {
  const {
    customerType: { trade: isTradeCustomer },
  } = useCustomerContext()

  const isMobile = useMediaQuery({
    query: breakpoints => `(max-device-width: ${breakpoints[2]})`,
  })

  // Cheapest
  const cheapestVariant = productVariant || product.cheapestVariant

  const cheapestVariantPrice =
    cheapestVariant?.price?.amount &&
    formatMoney(+cheapestVariant.price.amount, 'USD', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })

  const cheapestVariantPriceUnformatted = Number(cheapestVariant?.price?.amount) || 0

  const cheapestComparePrice =
    cheapestVariant?.compareAtPrice?.amount &&
    formatMoney(+cheapestVariant.compareAtPrice.amount, 'USD', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })

  const cheapestComparePriceUnformatted = Number(cheapestVariant?.compareAtPrice?.amount) || 0

  // Highest
  const highestVariant = productVariant || product.highestVariant

  const highestVariantPrice =
    highestVariant?.price?.amount &&
    formatMoney(+highestVariant.price.amount, 'USD', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })

  const highestVariantPriceUnformatted = Number(highestVariant?.price?.amount) || 0

  const highestComparePrice =
    highestVariant?.compareAtPrice?.amount &&
    formatMoney(+highestVariant.compareAtPrice.amount, 'USD', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })

  // todo - refactor the hooks
  const price = Number(cheapestVariantPriceUnformatted)
  const priceFormatted = isFabric
    ? formatMoney(price).replace('.00', '/YARD')
    : formatMoney(price, 'USD', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })

  const compareAtPrice = Number(cheapestComparePriceUnformatted)
  const isSale = Boolean(compareAtPrice)
  const compareAtPriceFormatted = compareAtPrice && formatMoney(compareAtPrice)
  const priceTradeCustomer = price * 0.8

  const isTradeAccountLabelVisible = Boolean(
    !isSale && isValidTradeCustomerDiscount({ isTradeCustomer, productType: product.productType })
  )

  const finalPrice = isTradeAccountLabelVisible
    ? formatMoney(priceTradeCustomer, 'USD', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    : priceFormatted

  const tradeLabelCustom = (isStartingAt: boolean) =>
    isTradeAccountLabelVisible ? (
      <Flex
        as={Text}
        sx={{
          flexDirection: `column`,
        }}
      >
        <CompareAtPrice
          price={priceFormatted}
          sxProps={{
            fontSize: ['inherit', null, 'inherit'],
            mb: [16],
            color: 'muted',
            mr: space.xxs,
          }}
        />
        <Text
          as="span"
          sx={{
            fontSize: ['18px'],
            lineHeight: [1.5],
            textTransform: 'uppercase',
            color: '#595858',
          }}
        >
          Trade Price: {isStartingAt && 'Starting at'}{' '}
          {isFabric ? `$${priceTradeCustomer}` : formatMoney(priceTradeCustomer)}
          {isFabric ? '/YARD' : ''}
        </Text>
      </Flex>
    ) : (
      <Box as="span">
        {isStartingAt && 'Starting at'} {priceFormatted}
      </Box>
    )
  const priceLabel = tradeLabelCustom(isStartingAt)

  const createRangePrice = () => {
    return (
      <Flex
        sx={{
          flexDirection: 'column',
        }}
      >
        {cheapestVariantPriceUnformatted !== highestVariantPriceUnformatted ? (
          <Fragment>
            <Text color="txt" sx={{ fontSize: isMobile && '12px' }}>
              {cheapestVariantPrice} - {highestVariantPrice}
            </Text>
            {(!isNaN(+cheapestVariant?.compareAtPrice?.amount) ||
              !isNaN(+cheapestVariant?.price?.amount)) &&
              (!isNaN(+highestVariant?.compareAtPrice?.amount) ||
                !isNaN(+highestVariant?.price?.amount)) && (
                <Text
                  marginTop={5}
                  color="txt"
                  sx={{
                    textDecoration: 'line-through',
                    color: 'grey',
                    fontSize: isMobile && '12px',
                  }}
                >
                  {!isNaN(+cheapestVariant?.compareAtPrice?.amount)
                    ? cheapestComparePrice
                    : cheapestVariantPrice}{' '}
                  -{' '}
                  {!isNaN(+highestVariant?.compareAtPrice?.amount)
                    ? highestComparePrice
                    : highestVariantPrice}
                </Text>
              )}
          </Fragment>
        ) : (
          <Fragment>
            <Text color="txt" sx={{ fontSize: isMobile && '12px' }}>
              {priceFormatted}
            </Text>
            <Text
              marginTop={5}
              color="txt"
              sx={{
                textDecoration: 'line-through',
                color: 'grey',
                fontSize: isMobile && '12px',
              }}
            >
              {highestComparePrice}
            </Text>
          </Fragment>
        )}
      </Flex>
    )
  }

  const RangePrice = () => {
    return (
      <Flex
        sx={{
          flexDirection: 'row',
        }}
      >
        {cheapestVariantPriceUnformatted !== highestVariantPriceUnformatted ? (
          <Fragment>
            <Text color="txt" sx={{ fontSize: isMobile && '14px', mr: '4px' }}>
              {cheapestVariantPrice} - {highestVariantPrice}
            </Text>
            {(!isNaN(+cheapestVariant?.compareAtPrice?.amount) ||
              !isNaN(+cheapestVariant?.price?.amount)) &&
              (!isNaN(+highestVariant?.compareAtPrice?.amount) ||
                !isNaN(+highestVariant?.price?.amount)) && (
                <Text
                  color="txt"
                  sx={{
                    textDecoration: 'line-through',
                    color: 'grey',
                    fontSize: '12px',
                  }}
                >
                  {!isNaN(+cheapestVariant?.compareAtPrice?.amount)
                    ? cheapestComparePrice
                    : cheapestVariantPrice}{' '}
                  -{' '}
                  {!isNaN(+highestVariant?.compareAtPrice?.amount)
                    ? highestComparePrice
                    : highestVariantPrice}
                </Text>
              )}
          </Fragment>
        ) : (
          <Fragment>
            <Text color="txt" sx={{ fontSize: isMobile && '14px', mr: '4px' }}>
              {priceFormatted}
            </Text>
            <Text
              color="txt"
              sx={{
                textDecoration: 'line-through',
                color: 'grey',
                fontSize: '12px',
              }}
            >
              {highestComparePrice}
            </Text>
          </Fragment>
        )}
      </Flex>
    )
  }

  return {
    price,
    rangePrice: createRangePrice(),
    RangePrice,
    priceWithoutTrade: price,
    isSale,
    priceLabel,
    compareAtPrice: compareAtPriceFormatted,
    finalPrice,
    isTradeCustomer,
  }
}

export default useProductVariantInfo
